import { useEffect, useState } from 'react';
import type { AllIndexWeights, IndexWeightConfig } from '../types';

interface SearchModeConfig {
  mode: 'regular' | 'research';
  name: string;
  description: string;
  enabledIndexes: string[];
  lastUpdated: string;
}

interface AllSearchModesConfig {
  regular: SearchModeConfig;
  research: SearchModeConfig;
}

interface WeightMultiEditProps {
  allWeights: AllIndexWeights;
  onSave: (config: IndexWeightConfig) => Promise<void>;
  onSaveMultiple: (configs: IndexWeightConfig[]) => Promise<void>;
  saving: boolean;
}

export function WeightMultiEdit({ allWeights, onSave: _onSave, onSaveMultiple, saving }: WeightMultiEditProps) {
  const [selectedIndexes, setSelectedIndexes] = useState<string[]>([]);
  const [localWeights, setLocalWeights] = useState<AllIndexWeights>(allWeights);
  const [hasChanges, setHasChanges] = useState(false);
  const [changedIndexes, setChangedIndexes] = useState<Set<string>>(new Set());
  const [sortedOrders, setSortedOrders] = useState<Map<string, number[]>>(new Map());
  const [selectedMode, setSelectedMode] = useState<'regular' | 'research'>('regular');
  const [searchModesConfig, setSearchModesConfig] = useState<AllSearchModesConfig | null>(null);
  const [loadingModes, setLoadingModes] = useState(true);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  // Fetch search modes configuration
  useEffect(() => {
    const fetchSearchModes = async () => {
      try {
        setLoadingModes(true);
        const response = await fetch(`${API_BASE_URL}/api/search-modes`);
        if (response.ok) {
          const data = await response.json();
          setSearchModesConfig(data);
        }
      } catch (error) {
        console.error('Failed to fetch search modes:', error);
      } finally {
        setLoadingModes(false);
      }
    };
    fetchSearchModes();
  }, [API_BASE_URL]);

  useEffect(() => {
    // Auto-select first 3 indexes with weights, filtered by selected mode
    if (allWeights.indices.length > 0 && searchModesConfig) {
      const enabledIndexes = searchModesConfig[selectedMode].enabledIndexes;
      const firstThree = allWeights.indices
        .filter((idx) => idx.weights && idx.weights.length > 0 && enabledIndexes.includes(idx.indexName))
        .slice(0, 3)
        .map((idx) => idx.indexName);
      setSelectedIndexes(firstThree);
    }
    setLocalWeights(allWeights);

    // Create initial sorted order for each index (only once)
    const orders = new Map<string, number[]>();
    allWeights.indices.forEach((idx) => {
      const sortedIndexes = idx.weights
        .map((weight, index) => ({ weight, index }))
        .sort((a, b) => {
          if (a.weight.enabled !== b.weight.enabled) return a.weight.enabled ? -1 : 1;
          return b.weight.weight - a.weight.weight;
        })
        .map((item) => item.index);
      orders.set(idx.indexName, sortedIndexes);
    });
    setSortedOrders(orders);
  }, [allWeights, selectedMode, searchModesConfig]);

  const toggleIndex = (indexName: string) => {
    setSelectedIndexes((prev) =>
      prev.includes(indexName) ? prev.filter((idx) => idx !== indexName) : [...prev, indexName]
    );
  };

  const handleWeightChange = (indexName: string, fieldIndex: number, newWeight: number) => {
    setLocalWeights((prev) => ({
      ...prev,
      indices: prev.indices.map((idx) => {
        if (idx.indexName === indexName) {
          const updatedWeights = [...idx.weights];
          updatedWeights[fieldIndex] = { ...updatedWeights[fieldIndex], weight: newWeight };
          return { ...idx, weights: updatedWeights };
        }
        return idx;
      }),
    }));
    setHasChanges(true);
    setChangedIndexes((prev) => new Set(prev).add(indexName));
  };

  const handleEnabledChange = (indexName: string, fieldIndex: number) => {
    setLocalWeights((prev) => ({
      ...prev,
      indices: prev.indices.map((idx) => {
        if (idx.indexName === indexName) {
          const updatedWeights = [...idx.weights];
          updatedWeights[fieldIndex] = {
            ...updatedWeights[fieldIndex],
            enabled: !updatedWeights[fieldIndex].enabled,
          };
          return { ...idx, weights: updatedWeights };
        }
        return idx;
      }),
    }));
    setHasChanges(true);
    setChangedIndexes((prev) => new Set(prev).add(indexName));
  };

  const handleSaveAll = async () => {
    // Collect all changed configs
    const configsToSave: IndexWeightConfig[] = [];
    for (const indexName of changedIndexes) {
      const config = localWeights.indices.find((idx) => idx.indexName === indexName);
      if (config) {
        configsToSave.push(config);
      }
    }

    // Save all configs in one batch to prevent multiple re-renders
    if (configsToSave.length > 0) {
      await onSaveMultiple(configsToSave);
    }

    setHasChanges(false);
    setChangedIndexes(new Set());
  };

  const handleExportJson = () => {
    const jsonStr = JSON.stringify(localWeights, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `field-weights-${selectedMode}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportJson = () => {
    try {
      setJsonError('');
      const parsed = JSON.parse(jsonInput);

      // Validate the structure
      if (!parsed.indices || !Array.isArray(parsed.indices)) {
        throw new Error('Invalid JSON: missing "indices" array');
      }

      // Validate each index has required fields
      for (const idx of parsed.indices) {
        if (!idx.indexName || !idx.weights || !Array.isArray(idx.weights)) {
          throw new Error('Invalid JSON: each index must have "indexName" and "weights" array');
        }

        // Validate weight values don't exceed maximum of 10
        for (const weight of idx.weights) {
          if (typeof weight.weight === 'number' && weight.weight > 10) {
            throw new Error(
              `Invalid weight: field "${weight.field}" in index "${idx.indexName || idx.displayName}" has weight ${weight.weight} (maximum is 10)`
            );
          }
          if (typeof weight.weight === 'number' && weight.weight < 0) {
            throw new Error(
              `Invalid weight: field "${weight.field}" in index "${idx.indexName || idx.displayName}" has weight ${weight.weight} (minimum is 0)`
            );
          }
        }
      }

      // Update config with imported data
      setLocalWeights(parsed);
      setHasChanges(true);
      // Mark all imported indexes as changed
      const importedIndexNames = new Set<string>(parsed.indices.map((idx: IndexWeightConfig) => idx.indexName));
      setChangedIndexes(importedIndexNames);
      setShowJsonImport(false);
      setJsonInput('');
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON format');
    }
  };

  const selectedConfigs = localWeights.indices.filter((idx) => selectedIndexes.includes(idx.indexName));

  const getWeightColor = (weight: number): string => {
    if (weight < 0.5) return '#ff4444';
    if (weight < 1.0) return '#ff8800';
    if (weight === 1.0) return '#888888';
    if (weight <= 1.5) return '#44aa44';
    if (weight <= 2.0) return '#2266dd';
    return '#8844dd';
  };

  return (
    <div className="space-y-6">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          background: var(--boost-color, #18181b);
        }
        input[type="range"]::-moz-range-thumb {
          background: var(--boost-color, #18181b);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-zinc-900">Multi-Index Field Weight Editor</h3>
          <p className="mt-1 text-sm text-zinc-600">
            Select multiple indexes to edit their field weights side-by-side. Adjust sliders and toggle fields across
            different content types to optimize search results.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportJson}
            className="px-3 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors flex items-center gap-2"
            title="Export field weights as JSON"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export
          </button>
          <button
            onClick={() => setShowJsonImport(true)}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            title="Import field weights from JSON"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Import
          </button>
        </div>
      </div>

      {/* JSON Import Modal */}
      {showJsonImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div>
                <h3 className="text-lg font-semibold text-zinc-900">Import Field Weights JSON</h3>
                <p className="text-sm text-zinc-600 mt-1">Paste your field weights configuration below</p>
              </div>
              <button
                onClick={() => {
                  setShowJsonImport(false);
                  setJsonInput('');
                  setJsonError('');
                }}
                className="text-zinc-400 hover:text-zinc-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`{\n  "indices": [\n    {\n      "indexName": "asmi-dl-books",\n      "displayName": "Digital Books",\n      "weights": [\n        { "field": "title", "weight": 10, "enabled": true },\n        { "field": "content", "weight": 2, "enabled": true }\n      ]\n    }\n  ]\n}`}
                className="w-full h-96 p-4 font-mono text-sm border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              {jsonError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">Error:</p>
                  <p className="text-sm text-red-700 mt-1">{jsonError}</p>
                </div>
              )}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Format Requirements</h4>
                <p className="text-sm text-blue-800 mb-2">
                  The JSON must have an <code className="bg-blue-100 px-1 rounded">indices</code> array.
                </p>
                <p className="text-sm text-blue-800">
                  Each index must have <code className="bg-blue-100 px-1 rounded">indexName</code> and{' '}
                  <code className="bg-blue-100 px-1 rounded">weights</code> array with{' '}
                  <code className="bg-blue-100 px-1 rounded">field</code>,{' '}
                  <code className="bg-blue-100 px-1 rounded">weight</code>, and{' '}
                  <code className="bg-blue-100 px-1 rounded">enabled</code> properties.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <button
                onClick={() => {
                  setShowJsonImport(false);
                  setJsonInput('');
                  setJsonError('');
                }}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImportJson}
                disabled={!jsonInput.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Import Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-base font-semibold text-blue-900 mb-4">How Field Weights Work</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Purpose</h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Field weights determine how much each document field contributes to the overall relevance score when users
              search your content.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How It's Applied</h4>
            <p className="text-sm text-zinc-700 leading-relaxed mb-2">
              <strong>Field Score = Weight × Query Match Quality</strong>
            </p>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Higher weights amplify matches in important fields like titles, while lower weights reduce the impact of
              less relevant fields.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Example</h4>
            <ul className="space-y-1">
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Title match: 3.0 × 0.95 = 2.85
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Content match: 1.0 × 0.75 = 0.75
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Keywords match: 2.0 × 0.60 = 1.20
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Search Mode Selector */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-zinc-900">Search Mode:</h4>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setSelectedMode('regular')}
            className={`px-4 py-3 rounded-lg text-left transition-all ${
              selectedMode === 'regular'
                ? 'bg-zinc-900 text-white shadow-lg'
                : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">🔍</span>
              <h3 className="text-sm font-semibold">General Search</h3>
            </div>
            <p className="text-xs opacity-75">All content types for general users</p>
          </button>

          <button
            type="button"
            onClick={() => setSelectedMode('research')}
            className={`px-4 py-3 rounded-lg text-left transition-all ${
              selectedMode === 'research'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">🔬</span>
              <h3 className="text-sm font-semibold">Research Mode</h3>
            </div>
            <p className="text-xs opacity-75">Academic & technical content only</p>
          </button>
        </div>
        <p className="text-xs text-zinc-500 italic">
          💡 Note: Only indexes enabled in the selected search mode are shown below. To enable/disable indexes for each
          mode, use the "Search Modes" section.
        </p>
      </div>

      {/* Index Selector with Save Button */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-sm font-medium text-zinc-900">
              Select Indexes to Compare:
              {searchModesConfig && (
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  ({searchModesConfig[selectedMode].enabledIndexes.length} indexes enabled in{' '}
                  {selectedMode === 'regular' ? 'General Search' : 'Research Mode'})
                </span>
              )}
            </h4>
          </div>

          {/* Save Button */}
          {hasChanges && (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={saving}
              className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {saving
                ? 'Saving...'
                : `Save Changes (${changedIndexes.size} ${changedIndexes.size === 1 ? 'index' : 'indexes'})`}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {loadingModes ? (
            <div className="text-sm text-zinc-500">Loading search modes...</div>
          ) : searchModesConfig ? (
            allWeights.indices
              .filter(
                (idx) =>
                  idx.weights &&
                  idx.weights.length > 0 &&
                  searchModesConfig[selectedMode].enabledIndexes.includes(idx.indexName)
              )
              .map((idx) => {
                const displayName =
                  idx.displayName ||
                  idx.indexName
                    .replace('asmi-', '')
                    .replace(/-/g, ' ')
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                return (
                  <button
                    key={idx.indexName}
                    type="button"
                    onClick={() => toggleIndex(idx.indexName)}
                    title={idx.indexName}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      selectedIndexes.includes(idx.indexName)
                        ? 'bg-zinc-900 text-white'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                    }`}
                  >
                    {displayName}
                  </button>
                );
              })
          ) : (
            <div className="text-sm text-zinc-500">Unable to load search modes configuration</div>
          )}
        </div>
      </div>

      {/* Content Grid */}
      {selectedConfigs.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center">
          <p className="text-sm text-zinc-600">Select indexes above to edit their field weights</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {selectedConfigs.map((config) => {
            const displayName =
              config.displayName ||
              config.indexName
                .replace('asmi-', '')
                .replace(/-/g, ' ')
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            return (
              <div key={config.indexName} className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
                {/* Column Header */}
                <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-3">
                  <h4 className="font-semibold text-zinc-900">{displayName}</h4>
                  <code className="text-xs text-zinc-500">{config.indexName}</code>
                </div>

                {/* Weights List */}
                <div className="max-h-[600px] overflow-y-auto p-4 space-y-4">
                  {(sortedOrders.get(config.indexName) || config.weights.map((_, i) => i))
                    .filter((fieldIndex) => config.weights[fieldIndex]) // Filter out undefined weights
                    .map((fieldIndex) => {
                      const weight = config.weights[fieldIndex];
                      return (
                        <div
                          key={fieldIndex}
                          className={`space-y-2 rounded-md border p-3 ${
                            weight.enabled ? 'border-zinc-200 bg-white' : 'border-zinc-100 bg-zinc-50'
                          }`}
                        >
                          {/* Field Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-sm font-medium ${weight.enabled ? 'text-zinc-900' : 'text-zinc-500'}`}
                              >
                                {weight.field}
                              </span>
                              {!weight.enabled && (
                                <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-xs text-zinc-600">
                                  Disabled
                                </span>
                              )}
                            </div>
                            {/* Toggle Switch */}
                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                checked={weight.enabled}
                                onChange={() => handleEnabledChange(config.indexName, fieldIndex)}
                                className="peer sr-only"
                              />
                              <div className="peer h-5 w-9 rounded-full bg-zinc-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-zinc-900 peer-checked:after:translate-x-4"></div>
                            </label>
                          </div>

                          {/* Slider and Number Input */}
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min="0"
                              max="10"
                              step="0.1"
                              value={weight.weight}
                              onChange={(e) =>
                                handleWeightChange(config.indexName, fieldIndex, parseFloat(e.target.value))
                              }
                              disabled={!weight.enabled}
                              className="flex-1 h-2 rounded-lg appearance-none disabled:opacity-50 cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 disabled:[&::-webkit-slider-thumb]:cursor-not-allowed [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md"
                              style={
                                {
                                  background: weight.enabled
                                    ? `linear-gradient(to right, ${getWeightColor(weight.weight)} 0%, ${getWeightColor(weight.weight)} ${(weight.weight / 10) * 100}%, #e4e4e7 ${(weight.weight / 10) * 100}%, #e4e4e7 100%)`
                                    : '#e4e4e7',
                                  ['--boost-color' as string]: weight.enabled
                                    ? getWeightColor(weight.weight)
                                    : '#a1a1aa',
                                } as React.CSSProperties
                              }
                            />
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={weight.weight}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0 && val <= 10) {
                                  handleWeightChange(config.indexName, fieldIndex, val);
                                }
                              }}
                              disabled={!weight.enabled}
                              className="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-900 disabled:bg-zinc-100 disabled:text-zinc-500"
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
